import { useEffect, useRef, useState } from 'react';
import { startAppUpdateWatch } from '../lib/appUpdateWatch';

/**
 * "Paraclete has been updated" — shown to a tab that is older than the
 * build on the server. See src/lib/appUpdateWatch.js for why a tab
 * needs telling: left open across a deploy, the next lazily loaded
 * screen it reaches for is gone, and the recovery reload takes the
 * form it was in with it.
 *
 * It asks; it does not act. "Reload" goes through the fresh-shell
 * reload main.jsx exposes, which also drops the service worker's cached
 * copy of the old page — a plain reload would come straight back here.
 * "Later" hides it for a while; the next check raises it again.
 */
const LATER_MS = 20 * 60 * 1000;

export default function AppUpdateBanner() {
  const [update, setUpdate] = useState(null);
  const [hidden, setHidden] = useState(false);
  const laterTimer = useRef(null);

  useEffect(() => startAppUpdateWatch({ onUpdate: setUpdate }), []);
  useEffect(() => () => { if (laterTimer.current) clearTimeout(laterTimer.current); }, []);

  if (!update || hidden) return null;

  const reload = () => {
    if (typeof window.__forceFreshAppShell === 'function') window.__forceFreshAppShell();
    else window.location.reload();
  };

  return (
    <div
      data-app-update-banner
      role="status"
      style={{
        position: 'fixed', left: '50%', bottom: 14, transform: 'translateX(-50%)',
        zIndex: 2000, maxWidth: 'calc(100vw - 32px)',
        display: 'inline-flex', alignItems: 'center', gap: 10,
        padding: '8px 10px 8px 14px', borderRadius: 0,
        background: '#0f172a', color: '#e2e8f0',
        border: '1px solid #334155', boxShadow: '0 8px 24px rgba(2, 6, 23, 0.45)',
        fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.3,
      }}
    >
      <span>Paraclete has been updated since this tab was opened. Reload to get the latest — finish what you are doing first.</span>
      <button
        type="button"
        data-app-update-reload
        onClick={reload}
        style={{
          background: '#2563eb', color: '#fff', border: 'none', borderRadius: 0,
          padding: '6px 10px', fontSize: '0.74rem', fontWeight: 800, cursor: 'pointer',
          textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
        }}
      >
        Reload
      </button>
      <button
        type="button"
        data-app-update-later
        onClick={() => {
          setHidden(true);
          if (laterTimer.current) clearTimeout(laterTimer.current);
          laterTimer.current = setTimeout(() => setHidden(false), LATER_MS);
        }}
        style={{
          background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 0,
          padding: '6px 10px', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        Later
      </button>
    </div>
  );
}
